/* =========================================================================
   at-reskin.js — Aussie Tonneaus PDP reskin behaviour.

   Vanilla JS, ES5-flavoured, no build step, no modules, no jQuery — matching
   the theme's existing conventions (jQuery is NOT a theme dependency; the only
   reference to it in the theme is Afterpay's vendor snippet loading its own
   namespaced copy).

   Contents
     0  Config + adapters  ..... garage app hook, cart adapter, money format
     1  Utilities
     2  Variant selection  ..... recomputes price / compare-at / save / Afterpay
                                 / SKU / sale flag from REAL Shopify variant
                                 prices. Nothing is hardcoded.
     3  Gallery
     4  Cart (AJAX Cart API) + drawer + cross-sell
     5  Sticky buy bar (scroll trigger)
     6  Garage panel
     7  Promo bar
     8  Judge.me glue
   ========================================================================= */
(function () {
  'use strict';

  /* -----------------------------------------------------------------------
     RE-ENTRY GUARD — do not remove.

     Three sections register this asset (at-reskin-pdp, at-reskin-header,
     at-reskin-promo). Shopify does not dedupe <script src>, and an identical
     src prevents a second DOWNLOAD, not a second EXECUTION. Without this
     guard boot() runs once per copy, which means N submit handlers on the
     product form (one Add to cart click posts N units to /cart/add.js), N
     delegated garage-toggle listeners (an even count leaves the panel looking
     dead because it opens and closes in the same click), N cross-sell
     handlers and N quantity handlers per drawer line.
     ----------------------------------------------------------------------- */
  if (window.AtReskin) { return; }

  /* Armed HERE, not at the bottom of the file. The flag used to be set by the
     `window.AtReskin = {...}` assignment at the end, which is reached only if
     boot() returns cleanly — so any throw inside boot() (a malformed
     [data-at-variants], a missing node in initCart, initJudgeme) left the flag
     unset and copies 2 and 3 re-ran the whole file, installing exactly the
     duplicate listeners this guard exists to prevent. The object is populated
     at the end rather than replaced. */
  window.AtReskin = {};

  /* =======================================================================
     0. CONFIG AND ADAPTERS
     ======================================================================= */

  var AT_RESKIN_CONFIG = {
    /* Set true to hand Add to cart back to the theme's own AJAX cart
       (assets/cart.js). If you do, also add `data-product-form: ''` to the
       form in sections/at-reskin-pdp.liquid — that is the attribute the
       theme's cart JS intercepts. Leaving both off (the default) means this
       file owns submission and the item cannot be added twice. */
    useThemeCart: false,

    /* Selector for cart count badges this file keeps in sync.

       CAREFUL: the theme's generic [data-cart-count] hook belongs to the
       Arenacommerce WISHLIST drawer (snippets/arn_cart.liquid renders
       <span class="number_wl" data-cart-count>), so writing the cart count
       into every [data-cart-count] would clobber the wishlist count. This
       selector, and the header badge itself, therefore stay on the reskin's
       own [data-at-cart-count]. Widen it to a SPECIFIC theme node
       (e.g. '.site-header .cart-count') once you have confirmed which nodes
       assets/cart.js owns — never to a bare [data-cart-count]. */
    countSelector: '[data-at-cart-count]',

    /* Milliseconds to wait for Judge.me to paint its badge before giving up
       and leaving the plain star fallback in place. */
    judgemeTimeout: 6000
  };

  /* -----------------------------------------------------------------------
     GARAGE — adapter onto the store's EXISTING Arenacommerce garage app.

     There is exactly one garage on this store and this is the seam onto it.
     Nothing here invents a second vehicle store: the read semantics, the
     value format, the storage key and the persistence endpoint below were all
     decoded from the garage script the storefront actually serves.

       storage key   localStorage['my-garage']  — a JSON ARRAY
       value format  "<vehicle-handle>|<cab-value>"
                     e.g. "ford-ranger-next-gen-jul-2022-current|dual-cab"
       current entry logged in  -> theme.customer.myGarage[0]    (FIRST)
                     guest      -> my-garage[my-garage.length-1] (LAST)
       endpoint      POST https://garage.arenacommerce.com/app/garage-status-filter
                     { customerID, value, case: 'update', shop }

     The read asymmetry (index 0 when logged in, last element for guests) is
     the app's own and is reproduced verbatim in savedEntry() — do not "tidy"
     it, the two lists are maintained in opposite orders.

     A normalised vehicle is:
       { value, handle, cab, cabLabel, title, label, name, range }
     `label` is the pill text and is produced by shortTitle(), the app's own
     formatter — the same function that yields the "FORD RANGER (JUL 2022 -
     CURRENT)" the merchant recognises from the stock header's black bar.
     ----------------------------------------------------------------------- */

  var GARAGE_MONTHS = {
    jan: 1, january: 1, feb: 1, february: 1, mar: 1, march: 1, apr: 1, april: 1,
    may: 1, jun: 1, june: 1, jul: 1, july: 1, aug: 1, august: 1, sep: 1,
    sept: 1, september: 1, oct: 1, october: 1, nov: 1, november: 1, dec: 1,
    december: 1
  };
  var GARAGE_OPEN_ENDED = { current: 1, now: 1, present: 1, onward: 1, onwards: 1, today: 1 };

  function isYearToken(t) { return /^(19|20)\d{2}$/.test(t || ''); }

  /* "sb" -> "SB", "px3" -> "PX3", "gen" -> "Gen". Short vowel-less tokens are
     acronyms and codes (SB / HB / NSB / BT / PX3 / VW), everything else is an
     ordinary word. */
  function titleCaseWord(w) {
    if (!w) return '';
    if (w.length <= 3 && !/[aeiou]/.test(w)) { return w.toUpperCase(); }
    return w.charAt(0).toUpperCase() + w.slice(1);
  }

  function humaniseSlug(slug) {
    if (!slug) return '';
    return String(slug).toLowerCase().split(/[-_]+/).filter(Boolean).map(titleCaseWord).join(' ');
  }

  /* A vehicle handle carries no punctuation, so the date range has to be found
     rather than parsed: the first month-followed-by-a-year, or the first bare
     year, opens it. Everything before that is the model name, everything from
     there on is bracketed so shortTitle() can lift it out again —
     "ford-ranger-next-gen-jul-2022-current" becomes
     "Ford Ranger Next Gen (Jul 2022 - Current)". */
  function titleFromHandle(handle) {
    if (!handle) return '';
    var tokens = String(handle).toLowerCase().split(/[-_]+/).filter(Boolean);
    if (!tokens.length) return '';

    /* A trailing tub code is dropped BEFORE the range is bracketed. shortTitle()
       strips /\s+(SB|HB|NSB)\s*$/ — a code at the END of the string — but every
       token after the date opener ends up INSIDE the brackets here, so
       "ford-ranger-jul-2022-current-sb" would become "... (Jul 2022 - Current SB)",
       the string would end in ')', the strip would never fire, and the pill would
       read FORD RANGER (JUL 2022 - CURRENT SB) where the app's own black bar
       reads FORD RANGER (JUL 2022 - CURRENT). */
    if (/^(sb|hb|nsb)$/.test(tokens[tokens.length - 1])) { tokens.pop(); }
    if (!tokens.length) return '';

    var start = -1;
    for (var i = 0; i < tokens.length; i++) {
      var t = tokens[i];
      var next = tokens[i + 1];
      if ((GARAGE_MONTHS[t] && (isYearToken(next) || GARAGE_OPEN_ENDED[next])) || isYearToken(t)) {
        start = i;
        break;
      }
    }

    var head = start === -1 ? tokens : tokens.slice(0, start);
    var tail = start === -1 ? [] : tokens.slice(start);

    /* A one- or two-character token that is not the first word is half of a
       compound model name that lost its hyphen in the handle — "isuzu-d-max"
       is "Isuzu D-Max", not three words. Rejoin it, or shortTitle's
       first-two-words rule truncates the model to "Isuzu D". */
    var words = [];
    for (var h = 0; h < head.length; h++) {
      var w = titleCaseWord(head[h]);
      if (words.length && w.length <= 2 && h < head.length - 1) {
        words.push(w + '-' + titleCaseWord(head[h + 1]));
        h++;
      } else {
        words.push(w);
      }
    }
    var name = words.join(' ');
    if (!tail.length) { return name; }

    /* Split the tail into the two ends of the range — a month, an open-ended
       word, or a year not already claimed by a preceding month starts a new
       end — then join them the way the app's own titles read. */
    var groups = [];
    var cur = [];
    for (var j = 0; j < tail.length; j++) {
      var tok = tail[j];
      var prev = tail[j - 1];
      var opens = GARAGE_MONTHS[tok] || GARAGE_OPEN_ENDED[tok] ||
                  (isYearToken(tok) && !(prev && GARAGE_MONTHS[prev]));
      if (opens && cur.length) { groups.push(cur); cur = []; }
      cur.push(tok);
    }
    if (cur.length) { groups.push(cur); }

    var range = groups.map(function (g) {
      return g.map(titleCaseWord).join(' ');
    }).join(' - ');

    return (name + ' (' + range + ')').trim();
  }

  /* The app's own shortTitle(), reproduced exactly. Drops a trailing tub code,
     keeps the bracketed date range, and keeps only the first two words of the
     model name so the pill reads "Ford Ranger (Jul 2022 - Current)". */
  function shortTitle(t) {
    t = (t || '').replace(/\s+(SB|HB|NSB)\s*$/, '').trim();
    var yr = (t.match(/\(([^)]*)\)/) || [])[0] || '';
    var words = t.replace(/\([^)]*\)/g, '').trim().split(/\s+/);
    var mm = words.slice(0, 2).join(' ');
    return (mm + (yr ? ' ' + yr : '')).trim() || t;
  }

  function titleWithoutRange(title) {
    return String(title || '')
      .replace(/\s+(SB|HB|NSB)\s*$/, '')
      .replace(/\([^)]*\)/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function rangeOfTitle(title) {
    var m = String(title || '').match(/\(([^)]*)\)/);
    return m ? m[1].trim() : '';
  }

  var AtReskinGarage = {
    /* The garage app's key. NOT a reskin key — writing anywhere else would be
       the second garage this design explicitly must not create. */
    STORAGE_KEY: 'my-garage',
    /* The key an earlier build of this file wrote to. Read ONCE, by
       migrateLegacy(), and then deleted — never used as a display fallback.
       See migrateLegacy() for why. */
    LEGACY_KEY: 'at_reskin_vehicle',
    ENDPOINT: 'https://garage.arenacommerce.com/app/garage-status-filter',

    /* The app's savedVehicle(), reproduced exactly. See the note above about
       the index-0 / last-element asymmetry. */
    savedEntry: function () {
      if (window.theme && theme.customer && theme.customer.myGarage && theme.customer.myGarage.length) {
        return theme.customer.myGarage[0];
      }
      /* Guests: the LAST entry is the current one. Routed through list() for
         its non-array guard — an older or alternate garage build stores a bare
         JSON STRING under this key, and indexing that like an array
         (l[l.length - 1]) returns its last CHARACTER, which normalise() then
         title-cased into a fabricated one-letter vehicle. The last-element read
         itself is the app's own and is unchanged. */
      var l = this.list();
      return l.length ? l[l.length - 1] : null;
    },

    /* The whole guest list, in the app's order (last = current). */
    list: function () {
      try {
        var l = JSON.parse(window.localStorage.getItem(this.STORAGE_KEY)) || [];
        return (l && typeof l !== 'string' && l.length !== undefined) ? l : [];
      } catch (e) {
        return [];
      }
    },

    /* Entries are "handle|cab" strings, but object entries are accepted on the
       way in so a future app build cannot break the header. */
    valueOfEntry: function (entry) {
      if (!entry) return '';
      if (typeof entry === 'string') return entry.trim();
      if (typeof entry === 'object') {
        return String(entry.value || entry.vehicle || entry.handle || '').trim();
      }
      return '';
    },

    titleOfEntry: function (entry) {
      if (entry && typeof entry === 'object') {
        return String(entry.title || entry.name || entry.vehicle_title || '').trim();
      }
      return '';
    },

    normalise: function (entry) {
      var value = this.valueOfEntry(entry);
      var given = this.titleOfEntry(entry);
      if (!value && !given) return null;

      var parts = String(value).split('|');
      var handle = (parts[0] || '').trim();
      var cab = (parts[1] || '').trim();

      /* A title carried on the entry always wins — it is the app's own copy.
         Only when there is nothing nicer is one derived from the handle. */
      var title = given || titleFromHandle(handle);
      if (!title) return null;

      var cabLabel = humaniseSlug(cab);
      var range = rangeOfTitle(title);

      return {
        value: value,
        handle: handle,
        cab: cab,
        cabLabel: cabLabel,
        title: title,
        /* the pill / vehicle-bar headline. The cab is deliberately NOT in it,
           matching how the stock black bar reads. */
        label: shortTitle(title),
        name: titleWithoutRange(title),
        range: [range, cabLabel].filter(Boolean).join(' · ')
      };
    },

    read: function () {
      return this.normalise(this.savedEntry());
    },

    /* ONE-SHOT MIGRATION off the key an earlier build of this file wrote to.
       It is deliberately not a fallback in read(): a legacy record carries no
       "handle|cab", so it can never be reconciled with the app, no code path
       ever cleared it, and it was painted whenever the real garage came back
       empty — i.e. for a customer who had just emptied their garage, or the
       next person on a shared browser. That is a wrong-vehicle fitment claim
       on the sticky bar and on every cart line. It also made read() truthy at
       boot, which stopped watch()'s poll before the real vehicle landed.

       So: if the record happens to hold a resolvable garage value and the
       garage is empty, write it properly; then delete the key either way. */
    migrateLegacy: function () {
      var raw = null;
      try { raw = window.localStorage.getItem(this.LEGACY_KEY); } catch (e) { return; }
      if (!raw) { return; }
      var v = null;
      try { v = JSON.parse(raw); } catch (e) { v = null; }
      if (v && typeof v === 'object' && this.isValue(v.value) && !this.savedEntry()) {
        this.write(v.value);
      }
      try { window.localStorage.removeItem(this.LEGACY_KEY); } catch (e) {}
    },

    /* Storefront facts the write path needs, rendered by the header section.
       Falls back to the globals the app itself reads. */
    appConfig: function () {
      var c = readJSON('[data-at-garage-config]') || {};
      return {
        shop: c.shop || (window.Shopify && window.Shopify.shop) || '',
        customerId: c.customerId ||
          (window.theme && theme.customer && theme.customer.id) || '',
        endpoint: c.endpoint || this.ENDPOINT
      };
    },

    /* An authoritative garage value is a slug on both sides of the pipe. A
       make/model/year typed into a select is NOT one, and writing it would put
       an entry in the garage that the app cannot resolve to a vehicle — so
       anything failing this is refused and the caller falls back to the app's
       own picker. */
    isValue: function (value) {
      return /^[a-z0-9]+(?:[-_.][a-z0-9]+)*\|[a-z0-9]+(?:[-_.][a-z0-9]+)*$/
        .test(String(value || '').trim().toLowerCase());
    },

    /* Set the current vehicle, exactly the way the site does it.
       Returns true when the value was accepted. When a request is sent the
       promise is left on `.pending`, so a caller about to navigate can wait
       for it instead of killing it mid-flight. */
    write: function (value) {
      /* Normalised ONCE, up front. isValue() tested a lower-cased COPY and the
         dedupe compared lower-cased copies, but the value stored, posted and
         unshifted into theme.customer.myGarage was the untouched input — so a
         mixed-case value passed in through window.AtReskin.garage.write() (the
         integration seam UPLOAD-MANIFEST.md documents) went into the garage in
         a form the app cannot resolve to a vehicle, which is precisely what
         isValue() exists to prevent. */
      value = String(value || '').trim().toLowerCase();
      this.pending = null;
      /* Whether the choice will survive a reload. saveGarageSelection() reads
         it before navigating — see the note there. */
      this.persisted = false;
      if (!this.isValue(value)) { return false; }

      var conf = this.appConfig();

      /* GUESTS ONLY. 'my-garage' is the guest list — the logged-in read never
         looks at it. Writing it while logged in left a second copy of the
         vehicle that diverged the moment the customer changed ute through the
         app's own picker on a non-reskin page, outlived logout, and was what
         got painted during the window before the app fills
         theme.customer.myGarage. The LAST entry is the current one; an
         existing entry is moved to the end rather than added again, so the
         list never carries the same vehicle twice. */
      if (!conf.customerId) {
        var list = this.list();
        var out = [];
        for (var i = 0; i < list.length; i++) {
          if (this.valueOfEntry(list[i]).toLowerCase() !== value) {
            out.push(list[i]);
          }
        }
        out.push(value);
        try {
          window.localStorage.setItem(this.STORAGE_KEY, JSON.stringify(out));
          this.persisted = true;
        } catch (e) { /* private mode or over quota — see saveGarageSelection() */ }
      }

      if (conf.customerId) {
        /* The server is the source of truth for a logged-in customer, so the
           choice survives the reload even if the POST is still in flight. */
        this.persisted = true;
        /* Logged in: the FIRST entry is the current one. Only reorder the
           app's in-page list when the app actually put one there —
           fabricating theme.customer would be inventing app state. */
        if (window.theme && theme.customer && theme.customer.myGarage &&
            theme.customer.myGarage.length !== undefined) {
          var g = [];
          for (var j = 0; j < theme.customer.myGarage.length; j++) {
            if (this.valueOfEntry(theme.customer.myGarage[j]).toLowerCase() !== value) {
              g.push(theme.customer.myGarage[j]);
            }
          }
          g.unshift(value);
          theme.customer.myGarage = g;
        }

        if (conf.shop && window.fetch) {
          try {
            this.pending = window.fetch(conf.endpoint, {
              method: 'POST',
              mode: 'cors',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                customerID: conf.customerId,
                value: value,
                case: 'update',
                shop: conf.shop
              })
            })['catch'](function (err) {
              window.console && console.warn('[at-reskin] garage persist failed', err);
            });
          } catch (e) { this.pending = null; }
        }
      }

      var vehicle = this.normalise(value);
      this.paint(vehicle);
      document.dispatchEvent(new CustomEvent('at:garage:change', {
        bubbles: true,
        detail: { vehicle: vehicle, value: value }
      }));
      return true;
    },

    /* Remove the CURRENT vehicle from the garage: the last guest entry in
       localStorage (the slot savedEntry() reads for guests), and the front of
       theme.customer.myGarage for logged-in customers (in-page only — the
       app's server copy has no documented remove call, so the next authorised
       write supersedes it). Repaints everything back to placeholders. */
    clearCurrent: function () {
      var entry = this.savedEntry();
      if (!entry) {
        /* Nothing left to remove — the theme's own Clear handler (a CAPTURE
           listener in js-file.liquid) empties the garage before this
           bubble-phase call when the dialog's Clear is the trigger. The pill,
           panel and clear buttons still have to repaint to placeholders, or
           the header keeps asserting a ute that is no longer saved. */
        this.paint(null);
        document.dispatchEvent(new CustomEvent('at:garage:change', { bubbles: true, detail: { vehicle: null, value: null } }));
        return false;
      }
      var value = this.valueOfEntry(entry).toLowerCase();
      if (window.theme && theme.customer && theme.customer.myGarage && theme.customer.myGarage.length) {
        var kept = [];
        for (var j = 0; j < theme.customer.myGarage.length; j++) {
          if (this.valueOfEntry(theme.customer.myGarage[j]).toLowerCase() !== value || kept.removedOne) { kept.push(theme.customer.myGarage[j]); }
          else { kept.removedOne = true; }
        }
        theme.customer.myGarage = kept;
      }
      try {
        var l = this.list().filter(function (x) {
          return AtReskinGarage.valueOfEntry(x).toLowerCase() !== value;
        });
        localStorage.setItem('my-garage', JSON.stringify(l));
      } catch (e) {}
      this.paint(null);
      document.dispatchEvent(new CustomEvent('at:garage:change', { bubbles: true, detail: { vehicle: null, value: null } }));
      return true;
    },

    /* Repaint every pill / bar / panel node from the current vehicle. With no
       vehicle the nodes fall back to the placeholder text the section
       rendered — never to a made-up ute. */
    paint: function (vehicle) {
      var v = vehicle || null;
      placeholderText('[data-at-garage-label]', v && v.label);
      placeholderText('[data-at-garage-name]', v && v.name);
      placeholderText('[data-at-garage-range]', v && v.range);
      each('[data-at-header]', function (el) {
        el.classList.toggle('at-has-vehicle', !!v);
      });
      each('[data-at-garage-clear]', function (btn) { btn.hidden = !v; });
      this.paintFitlines(document, v);
    },

    /* Fitment confirmation lines — the green "Fits your ..." in the sticky bar
       and on every cart line. These are CLAIMS about the customer's vehicle,
       so they are never typed into the theme editor: the editor holds only a
       template ("Fits your {name}") and the vehicle comes from the garage.
       With no vehicle chosen there is nothing true to say, so the node stays
       empty and hidden rather than asserting a fit for someone else's ute.

       `scope` exists because cart lines are cloned from a <template> after the
       initial paint — buildLine() calls this on each new node. */
    paintFitlines: function (scope, vehicle) {
      var v = vehicle || this.read();
      var name = v && (v.name || v.label);
      each('[data-at-garage-fitline]', function (el) {
        /* With no vehicle the node falls back to whatever it was rendered
           with, held in data-at-garage-fit-default. The sticky bar and the
           cart lines render empty, so they keep behaving exactly as before —
           nothing is ever asserted about a ute the customer has not chosen.
           The fitment card on the PDP renders the editor's fallback line when
           it has one, and this is what lets a real garage vehicle take
           precedence over that fallback without erasing it when there is none. */
        var fallback = el.getAttribute('data-at-garage-fit-default') || '';
        if (!name) {
          el.textContent = fallback;
          el.hidden = !fallback;
        } else {
          var tpl = el.getAttribute('data-at-garage-fit-template') || 'Fits your {name}';
          el.textContent = tpl
            .replace(/\{name\}/g, v.name || v.label || '')
            .replace(/\{label\}/g, v.label || v.name || '')
            .replace(/\{range\}/g, v.range || '');
          el.hidden = false;
        }
        /* The fitment card is the icon plus the text; hiding the text alone
           would leave a card with a lone ute glyph in it. */
        var card = el.closest ? el.closest('[data-at-fitcard]') : null;
        if (card) { card.hidden = el.hidden; }
      }, scope || document);
    },

    /* The app fills theme.customer.myGarage AFTER load — it is what populates
       the stock header's black bar, which is empty in the server HTML — so the
       first read can legitimately come back empty on a page the customer does
       have a vehicle for. Poll briefly, stop the moment one appears, and stay
       listening for changes made in another tab or by the app itself. */
    watch: function () {
      var self = this;
      var last = garageSignature(self.read());

      function repaint() {
        var v = self.read();
        var sig = garageSignature(v);
        if (sig === last) { return !!v; }
        last = sig;
        self.paint(v);
        return !!v;
      }

      /* The poll stops only once the authoritative source FOR THIS VISITOR has
         appeared, not merely once something is readable. For a logged-in
         customer that source is theme.customer.myGarage, which the app fills
         after load; stopping at the first readable value froze whatever the
         guest branch happened to return (a stale localStorage entry from an
         earlier visit) on screen for the life of the page, and nothing
         repainted afterwards — the 'storage' event does not fire in the tab
         that wrote, and 'load' may already have gone. A wrong ute asserted in
         "Fits your ..." is worse than the placeholder. Guests have no later
         source, so any value satisfies them and they keep the early exit. */
      function satisfied() {
        if (self.appConfig().customerId) {
          return !!(window.theme && theme.customer && theme.customer.myGarage &&
                    theme.customer.myGarage.length);
        }
        return !!self.read();
      }

      var tries = 0;
      var timer = window.setInterval(function () {
        tries++;
        repaint();
        if (satisfied() || tries >= 20) { window.clearInterval(timer); }
      }, 400);

      window.addEventListener('load', repaint);
      window.addEventListener('pageshow', repaint);
      window.addEventListener('storage', function (e) {
        if (!e.key || e.key === self.STORAGE_KEY) { repaint(); }
      });
      document.addEventListener('at:garage:change', function (e) {
        var v = (e.detail && e.detail.vehicle) || self.read();
        last = garageSignature(v);
        self.paint(v);
      });
    }
  };

  function garageSignature(v) {
    if (!v) return '';
    return [v.value, v.label, v.name, v.range].join('|');
  }

  /* Write `text` into every match, remembering what the section rendered so an
     empty garage restores the placeholder instead of blanking the node. */
  function placeholderText(selector, text) {
    each(selector, function (el) {
      if (!el.hasAttribute('data-at-placeholder')) {
        el.setAttribute('data-at-placeholder', (el.textContent || '').trim());
      }
      el.textContent = text || el.getAttribute('data-at-placeholder') || '';
    });
  }

  /* -----------------------------------------------------------------------
     CART ADAPTER — the seam onto the theme's existing cart.

     The theme ships assets/cart.js (+ cart-drawer.css, cart-dialog.css) which
     is NOT in this bundle and could not be read. Everything below talks to the
     official AJAX Cart API, which is what that file also uses, so the two
     views stay consistent. If cart.js turns out to expose its own add/refresh
     functions, point these three methods at them and nothing else changes.
     ----------------------------------------------------------------------- */
  var AtReskinCart = {
    add: function (items) {
      return postJSON(cfg('cartAddUrl', '/cart/add.js'), { items: items });
    },
    change: function (payload) {
      return postJSON(cfg('cartChangeUrl', '/cart/change.js'), payload);
    },
    get: function () {
      return fetch(cfg('cartJsUrl', '/cart.js'), {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      }).then(function (r) { return r.json(); });
    }
  };

  /* =======================================================================
     1. UTILITIES
     ======================================================================= */

  var root = document.querySelector('[data-at-pdp]');
  var CONFIG = readJSON('[data-at-config]') || {};
  var VARIANTS = readJSON('[data-at-variants]') || [];

  function readJSON(selector) {
    var el = document.querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function cfg(key, fallback) {
    return CONFIG[key] || fallback;
  }

  function each(selector, fn, scope) {
    var list = (scope || document).querySelectorAll(selector);
    for (var i = 0; i < list.length; i++) { fn(list[i], i); }
  }

  function one(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) { throw data; }
        return data;
      });
    });
  }

  /* Shopify's standard money formatter, driven by shop.money_format so the
     store's own currency formatting (AUD) is respected. Used ONLY for values
     that come back from the AJAX Cart API — every price rendered by Liquid
     already went through the theme's money-format snippet. */
  function formatMoney(cents, format) {
    var fmt = format || CONFIG.moneyFormat || '${{amount}}';
    if (typeof cents === 'string') { cents = cents.replace('.', ''); }
    var value = '';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function pad(number, size) {
      var s = '0000000000' + number;
      return s.substring(s.length - (size || 2));
    }

    function withDelimiters(number, precision, thousands, decimal) {
      precision = isNaN(precision) ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number === null) { return 0; }
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents_ = parts[1] ? decimal + parts[1] : '';
      return dollars + cents_;
    }

    var match = fmt.match(placeholder);
    switch (match && match[1]) {
      case 'amount':                 value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals':     value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator':     value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator':     value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ','); break;
      case 'amount_with_period_and_space_separator':  value = withDelimiters(cents, 2, ' ', '.'); break;
      default:                       value = withDelimiters(cents, 2); break;
    }
    void pad;

    /* The theme's money-format snippet uses money_without_trailing_zeros, so a
       whole-dollar price renders "$1,199" in Liquid. Without this the drawer
       would print "$1,199.00" for the same product on the same screen. The
       flag is derived in Liquid by probing that snippet — see the money parity
       note in sections/at-reskin-pdp.liquid. */
    if (CONFIG.dropZeroCents) { value = String(value).replace(/[.,]00$/, ''); }

    return fmt.replace(placeholder, value);
  }

  function variantById(id) {
    for (var i = 0; i < VARIANTS.length; i++) {
      if (String(VARIANTS[i].id) === String(id)) { return VARIANTS[i]; }
    }
    return null;
  }

  function setHTML(selector, html, scope) {
    each(selector, function (el) { el.innerHTML = html; }, scope);
  }

  function setText(selector, text, scope) {
    each(selector, function (el) { el.textContent = text; }, scope);
  }

  /* =======================================================================
     2. VARIANT SELECTION
     ======================================================================= */

  var currentVariant = null;

  function initVariants() {
    if (!root) return;

    var select = one('[data-at-variant-select]', root);
    currentVariant = variantById(select ? select.value : (VARIANTS[0] && VARIANTS[0].id));

    /* Event delegation — one listener for every option row. */
    root.addEventListener('click', function (e) {
      var row = e.target.closest ? e.target.closest('[data-at-option]') : null;
      if (row && root.contains(row)) {
        e.preventDefault();
        selectVariant(row.getAttribute('data-variant-id'));
        return;
      }
      /* Multi-option products: rows carry an option index + value; the
         combination resolves to a variant, and everything downstream reuses
         the single-option path. */
      var oval = e.target.closest ? e.target.closest('[data-at-optval]') : null;
      if (!oval || !root.contains(oval)) return;
      e.preventDefault();
      var sel = (currentVariant && currentVariant.opts) ? currentVariant.opts.slice() : [];
      sel[parseInt(oval.getAttribute('data-opt-index'), 10)] = oval.getAttribute('data-opt-value');
      var match = null;
      for (var vi = 0; vi < VARIANTS.length; vi++) {
        var vOpts = VARIANTS[vi].opts || [];
        var same = vOpts.length === sel.length;
        for (var oi = 0; same && oi < sel.length; oi++) { if (vOpts[oi] !== sel[oi]) same = false; }
        if (same) { match = VARIANTS[vi]; break; }
      }
      if (!match) return; /* combination not sold — leave selection unchanged */
      selectVariant(match.id);
      paintOptvalRows();
    });

    /* Multi-option rows: selected state + dynamic upcharge relative to the
       CURRENT selection (switching this value with everything else held). */
    function paintOptvalRows() {
      if (!currentVariant || !currentVariant.opts) return;
      each('[data-at-optval]', function (row) {
        var idx = parseInt(row.getAttribute('data-opt-index'), 10);
        var val = row.getAttribute('data-opt-value');
        var isSel = currentVariant.opts[idx] === val;
        row.classList.toggle('is-selected', isSel);
        row.setAttribute('aria-checked', isSel ? 'true' : 'false');
        var up = row.querySelector('[data-at-optval-up]');
        if (!up) return;
        var sel = currentVariant.opts.slice(); sel[idx] = val;
        var cand = null;
        for (var vi = 0; vi < VARIANTS.length; vi++) {
          var vOpts = VARIANTS[vi].opts || [];
          var same = vOpts.length === sel.length;
          for (var oi = 0; same && oi < sel.length; oi++) { if (vOpts[oi] !== sel[oi]) same = false; }
          if (same) { cand = VARIANTS[vi]; break; }
        }
        if (!cand || isSel || cand.price <= currentVariant.price) { up.hidden = true; return; }
        var delta = (cand.price - currentVariant.price) / 100;
        up.textContent = '+$' + (delta % 1 === 0 ? delta.toFixed(0) : delta.toFixed(2));
        up.hidden = false;
      }, root);
    }
    paintOptvalRows();

    /* Keyboard: the rows are role="radio", so arrow keys should move between
       them the way a native radio group does. */
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var row = e.target.closest ? e.target.closest('[data-at-option], [data-at-optval]') : null;
      if (!row) return;
      var rows = Array.prototype.slice.call(root.querySelectorAll('[data-at-option], [data-at-optval]'));
      var i = rows.indexOf(row);
      var next = (e.key === 'ArrowDown' || e.key === 'ArrowRight') ? i + 1 : i - 1;
      if (next < 0) next = rows.length - 1;
      if (next >= rows.length) next = 0;
      e.preventDefault();
      rows[next].focus();
      if (rows[next].hasAttribute('data-variant-id')) selectVariant(rows[next].getAttribute('data-variant-id'));
      else rows[next].click();
    });

    renderVariant(currentVariant);
  }

  function selectVariant(id) {
    var variant = variantById(id);
    if (!variant) return;
    currentVariant = variant;

    /* The theme's own idiom for driving a hidden select is to walk the options
       and set selectedIndex. It does NOT dispatch change — which is exactly
       why the theme's price/media sync silently fails on those code paths. We
       do both: set selectedIndex, then dispatch a bubbling change so any other
       listener still runs. */
    var select = one('[data-at-variant-select]', root);
    if (select) {
      var opts = select.options;
      for (var opt, j = 0; (opt = opts[j]); j++) {
        if (String(opt.value) === String(variant.id)) { select.selectedIndex = j; break; }
      }
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    renderVariant(variant);

    /* Keep the address bar honest without adding a history entry. */
    if (variant.url && window.history && window.history.replaceState) {
      window.history.replaceState({}, '', variant.url);
    }

    document.dispatchEvent(new CustomEvent('at:variant:change', {
      bubbles: true, detail: { variant: variant }
    }));
  }

  function renderVariant(variant) {
    if (!variant || !root) return;

    /* selected state */
    each('[data-at-option]', function (row) {
      var on = String(row.getAttribute('data-variant-id')) === String(variant.id);
      row.classList.toggle('is-selected', on);
      row.setAttribute('aria-checked', on ? 'true' : 'false');
    }, root);

    /* price, compare-at, save badge — every string was formatted by the
       theme's money-format snippet at render time */
    setHTML('[data-at-price]', variant.price_f, root);
    setHTML('[data-at-sticky-price]', variant.price_f, root);
    setHTML('[data-at-afterpay]', variant.afterpay_f, root);
    setText('[data-at-sku]', variant.sku || '', root);

    /* The Part number node exists whenever ANY variant carries a SKU, so it
       is here to be repainted. Hide the label and the `·` separator with it
       when the selected variant has none — clearing only the inner text would
       leave "... reviews · Part " with nothing after it. */
    each('[data-at-sku-part], [data-at-sku-sep]', function (el) {
      el.hidden = !variant.sku;
    }, root);

    if (variant.has_sale) {
      setHTML('[data-at-compare]', variant.compare_f, root);
      setHTML('[data-at-sticky-compare]', variant.compare_f, root);
      setHTML('[data-at-save]', variant.save_f, root);
      setText('[data-at-sale-pct]', variant.percent + '%', root);
    }

    /* Sale elements are rendered by Liquid only when the SELECTED variant is
       on sale. If the customer switches to a variant with a different sale
       state, the nodes have to appear/disappear. `.at-lowest` matches BOTH
       "Lowest price in 30 days" flags — the one in the buy block (mobile) and
       the one in the sticky bar. */
    each('[data-at-sale-group], [data-at-saleflag], [data-at-sticky-compare], .at-lowest', function (el) {
      el.hidden = !variant.has_sale;
    }, root);

    /* availability */
    each('[data-at-atc]', function (btn) {
      btn.disabled = !variant.available;
      var label = btn.querySelector('[data-at-atc-label]');
      var text = variant.available ? cfg('addToCartLabel', 'Add to cart') : cfg('soldOutLabel', 'Sold out');
      if (label) { label.textContent = text; } else { btn.textContent = text; }
    }, root);

    /* variant image — pass the real srcset through so a phone does not
       download the 1400w hero on every variant change */
    if (variant.media_id) {
      swapHero(variant.image, variant.image_srcset, variant.media_id);
      each('[data-at-sticky-thumb]', function (el) {
        if (variant.image) { el.src = variant.image; }
      }, root);
    }
  }

  /* =======================================================================
     3. GALLERY
     ======================================================================= */

  function initGallery() {
    if (!root) return;
    root.addEventListener('click', function (e) {
      var thumb = e.target.closest ? e.target.closest('[data-at-thumb]') : null;
      if (!thumb) return;
      e.preventDefault();
      var embed = thumb.getAttribute('data-video-embed');
      var vsrc = thumb.getAttribute('data-video-src');
      if (embed || vsrc) {
        showHeroVideo(embed, vsrc, thumb.getAttribute('data-media-id'));
        return;
      }
      swapHero(
        thumb.getAttribute('data-full'),
        thumb.getAttribute('data-full-srcset'),
        thumb.getAttribute('data-media-id')
      );
    });
  }

  /* Video thumbs put a real player in the hero; picking any image thumb (or
     a variant change calling swapHero) tears it down again, which also stops
     playback — an emptied iframe/video can't keep making noise. */
  function heroVideoEl() { return one('[data-at-hero-video]', root); }

  function clearHeroVideo() {
    var v = heroVideoEl();
    if (v) { v.innerHTML = ''; v.hidden = true; }
  }

  function showHeroVideo(embed, vsrc, mediaId) {
    var v = heroVideoEl();
    if (!v) return;
    if (embed) {
      var f = document.createElement('iframe');
      f.src = embed;
      f.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
      f.setAttribute('allowfullscreen', '');
      f.setAttribute('title', 'Product video');
      v.innerHTML = '';
      v.appendChild(f);
    } else {
      var m = document.createElement('video');
      m.src = vsrc;
      m.controls = true;
      m.autoplay = true;
      m.playsInline = true;
      v.innerHTML = '';
      v.appendChild(m);
    }
    v.hidden = false;
    each('[data-at-thumb]', function (t) {
      t.classList.toggle('is-active', String(t.getAttribute('data-media-id')) === String(mediaId));
    }, root);
  }

  function swapHero(src, srcset, mediaId) {
    clearHeroVideo();
    var hero = one('[data-at-hero]', root);
    if (hero && src) {
      hero.setAttribute('src', src);
      if (srcset) { hero.setAttribute('srcset', srcset); }
      else { hero.removeAttribute('srcset'); }
    }
    each('[data-at-thumb]', function (t) {
      t.classList.toggle('is-active', String(t.getAttribute('data-media-id')) === String(mediaId));
    }, root);
  }

  /* =======================================================================
     4. CART
     ======================================================================= */

  var lastFocused;

  /* Resolved at USE time, never cached at boot. The theme customizer replaces
     a section's whole DOM when the merchant edits it, and scripts re-inserted
     that way do not re-execute — so a reference captured at boot goes on
     pointing at a detached node. A cached [data-at-drawer] is truthy, so the
     header cart button still called preventDefault() and then opened nothing:
     the cart looked dead in the exact tool the merchant configures it with. */
  function drawerEl() { return one('[data-at-drawer]'); }
  function lineTemplateEl() { return one('[data-at-line-template]'); }

  function initCart() {

    /* Add to cart — both the main button and the sticky bar button submit the
       same form, so one submit handler covers both. */
    each('[data-at-form]', function (form) {
      form.addEventListener('submit', function (e) {
        if (AT_RESKIN_CONFIG.useThemeCart) return; /* let cart.js handle it */
        e.preventDefault();
        addCurrentToCart(form);
      });
    });

    /* Open / close */
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t.closest) return;

      /* Only swallow the click when there is a drawer to open. The drawer is
         rendered by the PDP section alone, so on collections, the home page or
         any non-reskin product the header cart must stay a plain link to
         /cart rather than a button that does nothing. */
      if (t.closest('[data-at-cart-open]')) {
        if (!drawerEl()) return;
        e.preventDefault();
        openDrawer();
        return;
      }
      if (t.closest('[data-at-drawer-close]')) { e.preventDefault(); closeDrawer(); return; }

      var toggle = t.closest('[data-at-addon-toggle]');
      if (toggle) { e.preventDefault(); toggleAddon(toggle); return; }

      var down = t.closest('[data-at-qty-down]');
      var up = t.closest('[data-at-qty-up]');
      var remove = t.closest('[data-item-remove]');
      if (down || up || remove) {
        e.preventDefault();
        var line = t.closest('[data-line-item]');
        if (!line) return;
        var key = line.getAttribute('data-item-key');
        var qty = parseInt(line.getAttribute('data-quantity'), 10) || 1;
        var next = remove ? 0 : (up ? qty + 1 : qty - 1);
        changeLine(key, next);
      }
    });

    document.addEventListener('keydown', function (e) {
      var d = drawerEl();
      if (e.key === 'Escape' && d && !d.hidden) { closeDrawer(); }
    });

    /* Paint the drawer once on load so a returning customer's cart is right.
       This is also what makes the header count true on every page the reskin
       header renders on: cart.item_count comes from Liquid, and /cart.js
       confirms it. */
    refreshCart();

    /* Back/forward cache restores the DOM as it was, count included, so the
       count has to be re-read rather than trusted after a bfcache restore. */
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) { refreshCart(); }
    });
  }

  function addCurrentToCart(form) {
    var select = one('[data-at-variant-select]', form);
    var id = select ? select.value : (currentVariant && currentVariant.id);
    if (!id) return;

    each('[data-at-atc]', function (b) { b.classList.add('is-loading'); });

    AtReskinCart.add([{ id: Number(id), quantity: 1 }])
      .then(function () { return refreshCart(); })
      .then(function () {
        if (CONFIG.openCartOnAdd !== false) { openDrawer(); }
        /* Fitment is acknowledged the moment the customer buys — it is never
           a gate, it just stops nagging. */
        root && root.classList.add('at-fitment-acknowledged');
      })
      .catch(function (err) {
        window.console && console.error('[at-reskin] add to cart failed', err);
        var msg = (err && (err.description || err.message)) || 'Sorry, that could not be added.';
        window.alert(msg);
      })
      .then(function () {
        each('[data-at-atc]', function (b) { b.classList.remove('is-loading'); });
      });
  }

  function changeLine(key, quantity) {
    if (!key) return;
    setBusy(true);
    AtReskinCart.change({ id: key, quantity: quantity })
      .then(function () { return refreshCart(); })
      .catch(function (err) { window.console && console.error('[at-reskin] cart change failed', err); })
      .then(function () { setBusy(false); });
  }

  function toggleAddon(button) {
    var row = button.closest('[data-at-addon]');
    if (!row) return;
    var variantId = row.getAttribute('data-variant-id');
    var added = button.classList.contains('is-added');
    button.disabled = true;

    var work = added
      ? findLineKey(variantId).then(function (key) { return key ? AtReskinCart.change({ id: key, quantity: 0 }) : null; })
      : AtReskinCart.add([{ id: Number(variantId), quantity: 1 }]);

    work
      .then(function () { return refreshCart(); })
      .catch(function (err) { window.console && console.error('[at-reskin] cross-sell failed', err); })
      .then(function () { button.disabled = false; });
  }

  function findLineKey(variantId) {
    return AtReskinCart.get().then(function (cart) {
      for (var i = 0; i < cart.items.length; i++) {
        if (String(cart.items[i].variant_id) === String(variantId)) { return cart.items[i].key; }
      }
      return null;
    });
  }

  function refreshCart() {
    return AtReskinCart.get().then(function (cart) {
      renderDrawer(cart);
      syncCount(cart.item_count);

      /* Tell the rest of the theme. If assets/cart.js listens for its own
         event name instead, add it here — this is the one place cart state
         leaves this file. */
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
      return cart;
    });
  }

  function syncCount(count) {
    each(AT_RESKIN_CONFIG.countSelector, function (el) { el.textContent = count; });
    /* The cart link's accessible name has to carry the count. A static
       aria-label on the anchor overrides all descendant text, so a screen
       reader announced "Cart, link" whether the cart held 0 items or 5. */
    each('[data-at-cart-open]', function (el) {
      var n = Number(count) || 0;
      el.setAttribute('aria-label', n === 1 ? 'Cart, 1 item' : 'Cart, ' + n + ' items');
    });
  }

  function renderDrawer(cart) {
    var drawer = drawerEl();
    if (!drawer) return;

    var items = one('[data-at-drawer-items]', drawer);
    var empty = one('[data-at-drawer-empty]', drawer);
    if (items) { items.innerHTML = ''; }

    var compareMap = buildCompareMap();
    var saved = 0;

    if (cart.items.length === 0) {
      if (empty) empty.hidden = false;
    } else {
      if (empty) empty.hidden = true;
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        var node = buildLine(item);
        if (node && items) { items.appendChild(node); }

        var compare = compareMap[String(item.variant_id)];
        if (compare && compare > item.price) {
          saved += (compare - item.price) * item.quantity;
        }
      }
    }

    /* savings row — sale only, exactly like every other sale element */
    var savedRow = one('[data-at-drawer-saved]', drawer);
    if (savedRow) {
      savedRow.hidden = saved <= 0;
      var amount = one('[data-at-drawer-saved-amount]', savedRow);
      if (amount) { amount.textContent = formatMoney(saved); }
    }

    setText('[data-at-drawer-subtotal]', formatMoney(cart.items_subtotal_price), drawer);

    /* cross-sell Add / Added state */
    each('[data-at-addon]', function (row) {
      var id = row.getAttribute('data-variant-id');
      var inCart = false;
      for (var k = 0; k < cart.items.length; k++) {
        if (String(cart.items[k].variant_id) === String(id)) { inCart = true; break; }
      }
      var btn = one('[data-at-addon-toggle]', row);
      if (!btn) return;
      btn.classList.toggle('is-added', inCart);
      btn.textContent = inCart
        ? (btn.getAttribute('data-label-added') || 'Added')
        : (btn.getAttribute('data-label-add') || 'Add');
    }, drawer);
  }

  function buildCompareMap() {
    var map = {};
    for (var i = 0; i < VARIANTS.length; i++) {
      if (VARIANTS[i].compare_at_price) { map[String(VARIANTS[i].id)] = VARIANTS[i].compare_at_price; }
    }
    each('[data-at-addon]', function (row) {
      var c = parseInt(row.getAttribute('data-compare-at'), 10);
      if (c) { map[String(row.getAttribute('data-variant-id'))] = c; }
    });
    return map;
  }

  function buildLine(item) {
    var lineTemplate = lineTemplateEl();
    if (!lineTemplate) return null;
    var frag = lineTemplate.content.cloneNode(true);
    var node = frag.querySelector('[data-line-item]');
    if (!node) return null;

    node.setAttribute('data-item-key', item.key);
    node.setAttribute('data-quantity', item.quantity);

    var img = node.querySelector('[data-item-image]');
    if (img) {
      if (item.image) {
        img.src = item.image.replace(/(\.[a-z]+(\?.*)?)$/i, '_264x$1');
        img.alt = item.product_title || '';
      } else {
        img.remove();
      }
    }

    var title = node.querySelector('[data-item-title]');
    if (title) {
      /* The design uses the short name for this page's own product — the full
         title ("Wildback Soft Ute Canopy With Ladder Racks") wraps to three
         lines in a 452px drawer. Other products keep their own title. */
      var isThisProduct = CONFIG.productId && String(item.product_id) === String(CONFIG.productId);
      title.textContent = (isThisProduct && CONFIG.shortName) ? CONFIG.shortName : item.product_title;
      title.href = item.url;
    }

    /* variant + part number, per the design */
    var meta = node.querySelector('[data-item-meta]');
    if (meta) {
      var bits = [];
      /* Single-variant products: the Cart API usually returns variant_title
         null, but "Default Title" leaks through on some line shapes and it is
         never something to show a customer. */
      if (item.variant_title && item.variant_title !== 'Default Title') bits.push(item.variant_title);
      if (item.sku) bits.push(item.sku);
      meta.textContent = bits.join(' · ');
      if (!bits.length) meta.remove();
    }

    /* Fitment line — cloned nodes never saw the initial garage paint. */
    AtReskinGarage.paintFitlines(node);

    var qty = node.querySelector('[data-quantity]');
    if (qty) { qty.textContent = item.quantity; }

    var total = node.querySelector('[data-item-total]');
    if (total) { total.textContent = formatMoney(item.final_line_price); }

    /* was-price precedence copied from sections/cart-template.liquid:
       original_price, unless the variant's compare-at is higher */
    var was = node.querySelector('[data-item-was]');
    if (was) {
      var compare = buildCompareMap()[String(item.variant_id)] || 0;
      var wasPrice = Math.max(item.original_price || 0, compare);
      if (wasPrice > item.final_price) {
        was.textContent = formatMoney(wasPrice * item.quantity);
        was.hidden = false;
      } else {
        was.hidden = true;
      }
    }

    return node;
  }

  function setBusy(state) {
    var busy = one('[data-at-drawer-busy]');
    if (busy) { busy.hidden = !state; }
  }

  function openDrawer() {
    var drawer = drawerEl();
    if (!drawer) return;
    lastFocused = document.activeElement;
    drawer.hidden = false;
    document.body.classList.add('at-drawer-open');
    var close = one('[data-at-drawer-close]', drawer);
    if (close) { close.focus(); }
  }

  function closeDrawer() {
    var drawer = drawerEl();
    if (!drawer) return;
    drawer.hidden = true;
    document.body.classList.remove('at-drawer-open');
    if (lastFocused && lastFocused.focus) { lastFocused.focus(); }
  }

  /* =======================================================================
     5. STICKY BUY BAR — mobile reveal
     ======================================================================= */

  function initStickyBar() {
    var bar = one('[data-at-stickybar]');
    var marker = one('[data-at-price-marker]');
    if (!bar || !marker) return;

    /* The prototype compared bounding rects on a scroll listener. In a real
       viewport an IntersectionObserver does the same job without running on
       every frame; the rect comparison is kept as the fallback. */
    /* The mobile bar is position:fixed, so while it is on screen it sits over
       the last ~66px of the page (the Help CTA card and the footer). The body
       class below lets CSS reserve that space — but only while the bar is
       actually revealed, so the page carries no dead space before then. */
    function reveal(past) {
      bar.classList.toggle('is-visible', past);
      document.body.classList.toggle('at-stickybar-visible', past);
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          /* Visible once the marker has left the top of the viewport. */
          reveal(entries[i].boundingClientRect.bottom < 0);
        }
      }, { threshold: 0, rootMargin: '0px' });
      io.observe(marker);
    } else {
      var onScroll = function () {
        reveal(marker.getBoundingClientRect().bottom < 0);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }

  /* =======================================================================
     6. GARAGE PANEL
     ======================================================================= */

  function initGarage() {
    /* The panel is looked up on every event, never captured here. The theme
       customizer replaces the header section's DOM when the merchant edits it,
       and a captured node would leave the vehicle pill toggling a panel that is
       no longer on the page — the garage looking completely broken in the one
       tool the merchant configures it with. */
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var panel = one('[data-at-garage-panel]');

      var toggle = e.target.closest('[data-at-garage-toggle]');
      if (toggle) {
        e.preventDefault();
        /* The vehicle pill (and the mobile vehicle bar) is TWO components in
           one button, split at the divider. LEFT half — truck + name — loads
           the saved ute's products page (garage panel when nothing is
           saved). RIGHT half — the three dots — opens the Find Your Fit
           dialog on its landing screen: the confirmation with a saved ute,
           the start screen without (see prepareNavOpen). The split is
           GEOMETRIC for clicks that land on the button's own frame (padding,
           gaps): everything left of the divider is the products half,
           everything at or right of it is the change half — no dead zones,
           so neither half ever swallows the other's clicks. Keyboard
           activation (no coordinates) performs the primary action, the
           products half. The panel's own Close button also carries this
           attribute and must stay a plain toggle — hence the class check. */
        var isPill = toggle.classList.contains('at-vpill') || toggle.classList.contains('at-vbar');
        if (isPill) {
          var goZone;
          var pillMain = toggle.querySelector('[data-at-pill-main]');
          var pillDots = toggle.querySelector('.at-vpill__dots');
          if (e.target.closest('.at-vpill__dots, .at-vpill__divider')) {
            goZone = false;
          } else if (pillMain && e.target.closest('[data-at-pill-main]')) {
            goZone = true;
          } else if (pillDots && e.clientX) {
            var pillBoundary = toggle.querySelector('.at-vpill__divider') || pillDots;
            goZone = e.clientX < pillBoundary.getBoundingClientRect().left;
          } else {
            goZone = true;
          }
          if (!goZone) {
            if (panel) { setGarageOpen(panel, false); }
            openVehicleFilter(filterSrc(), true);
            return;
          }
          var pillEntry = '';
          try { pillEntry = AtReskinGarage.valueOfEntry(AtReskinGarage.savedEntry()) || ''; } catch (err) {}
          if (pillEntry) {
            var pillUrl = garageCollectionUrl(pillEntry);
            if (pillUrl) { window.location.href = pillUrl; return; }
          }
        }
        if (!panel) return;
        setGarageOpen(panel, panel.hidden);
        /* Only one of the two mobile overlays at a time: the burger menu and
           the garage panel drop from the same offset and would otherwise sit
           on top of each other. */
        var nav = one('[data-at-menu]');
        if (nav && !panel.hidden) { closeMenu(nav); }
        return;
      }

      if (e.target.closest('[data-at-garage-clear]')) {
        e.preventDefault();
        AtReskinGarage.clearCurrent();
        return;
      }

      /* The Find Your Fit dialog's own Clear resets its form back to the
         start screen (theme handler in js-file.liquid — the dialog stays
         open); the customer reads it as "clear my ute", so the SAVED
         selection goes with it — otherwise the pill keeps asserting the old
         vehicle. */
      if (e.target.closest('.js-btn-clear')) {
        AtReskinGarage.clearCurrent();
        /* no return — the theme's own handler still resets the form */
      }

      var submit = e.target.closest('[data-at-garage-submit]');
      if (submit) {
        e.preventDefault();
        saveGarageSelection(submit);
        return;
      }

      /* click-away */
      if (panel && !panel.hidden && !e.target.closest('[data-at-garage-panel]')) {
        setGarageOpen(panel, false);
      }
    });

    document.addEventListener('keydown', function (e) {
      var panel = one('[data-at-garage-panel]');
      if (e.key === 'Escape' && panel && !panel.hidden) { setGarageOpen(panel, false); }
    });

    /* menu toggle lives here too — same delegated listener budget */
    document.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target.closest('[data-at-menu-toggle]') : null;
      if (!t) return;
      e.preventDefault();
      var nav = one('[data-at-menu]');
      if (!nav) return;
      var open = !nav.classList.contains('is-open');
      nav.classList.toggle('is-open', open);
      t.setAttribute('aria-expanded', open ? 'true' : 'false');
      /* see the note in the garage toggle above */
      var panel = one('[data-at-garage-panel]');
      if (open && panel && !panel.hidden) { setGarageOpen(panel, false); }
    });

    initGarageSelects();

    /* One-shot: clears the old reskin-only key before anything is painted, so
       a stale record can never reach the pill. */
    AtReskinGarage.migrateLegacy();

    /* First paint from the real garage, then keep watching — the app fills
       theme.customer.myGarage after load, so one read at boot is not enough. */
    AtReskinGarage.paint(AtReskinGarage.read());
    AtReskinGarage.watch();
  }

  function closeMenu(nav) {
    nav.classList.remove('is-open');
    each('[data-at-menu-toggle]', function (t) { t.setAttribute('aria-expanded', 'false'); });
  }

  function setGarageOpen(panel, open) {
    panel.hidden = !open;
    each('[data-at-garage-toggle]', function (t) {
      if (t.hasAttribute('aria-expanded')) {
        t.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
    });
  }

  /* -----------------------------------------------------------------------
     The panel's three selects.

     They are only ever populated from AUTHORITATIVE garage values — the
     "<handle>|<cab>" strings the app itself stores — supplied by the header
     section's "Garage vehicles" setting. Make and Model are filters derived
     from those values; the third select carries the whole value, so whatever
     the customer picks is something the garage app can resolve.

     With no values configured the selects hide themselves and the CTA falls
     through to the site's own vehicle picker. That is deliberate: a
     make/model/year assembled from free text is NOT a garage value, and
     writing one would put an unresolvable vehicle in the customer's garage.
     ----------------------------------------------------------------------- */
  function initGarageSelects() {
    var fields = one('[data-at-garage-fields]');
    if (!fields) return;

    var raw = readJSON('[data-at-garage-vehicles]') || [];
    var items = [];
    for (var i = 0; i < raw.length; i++) {
      var v = AtReskinGarage.normalise(raw[i]);
      if (!v || !AtReskinGarage.isValue(v.value)) { continue; }
      var words = v.name.split(/\s+/);
      v.make = words[0] || v.name;
      v.model = words.slice(1).join(' ') || v.name;
      v.rangeLabel = v.range || v.title;
      items.push(v);
    }

    if (!items.length) {
      fields.hidden = true;
      return;
    }
    fields.hidden = false;

    var makeSel = one('[data-at-garage-field="make"]', fields);
    var modelSel = one('[data-at-garage-field="model"]', fields);
    var yearSel = one('[data-at-garage-field="year"]', fields);
    if (!makeSel || !modelSel || !yearSel) return;

    fill(makeSel, unique(items, 'make'), null);
    fill(modelSel, [], null);
    fill(yearSel, [], null);

    makeSel.addEventListener('change', function () {
      var models = unique(filterBy(items, 'make', makeSel.value), 'model');
      fill(modelSel, models, null);
      fill(yearSel, [], null);
      /* One model for the make — choose it so the customer only makes the
         choices that are actually choices. */
      if (models.length === 1) { modelSel.value = models[0]; onModel(); }
    });

    modelSel.addEventListener('change', onModel);

    function onModel() {
      var matches = filterBy(filterBy(items, 'make', makeSel.value), 'model', modelSel.value);
      var opts = [];
      for (var k = 0; k < matches.length; k++) {
        opts.push({ value: matches[k].value, label: matches[k].rangeLabel });
      }
      fill(yearSel, opts, null);
      if (opts.length === 1) { yearSel.value = opts[0].value; }
    }

    /* Pre-select the customer's current vehicle so the panel opens showing
       what they already have rather than an empty form. */
    var current = AtReskinGarage.read();
    if (current && current.value) {
      for (var m = 0; m < items.length; m++) {
        if (items[m].value === current.value) {
          makeSel.value = items[m].make;
          fill(modelSel, unique(filterBy(items, 'make', items[m].make), 'model'), null);
          modelSel.value = items[m].model;
          onModel();
          yearSel.value = items[m].value;
          break;
        }
      }
    }

    function unique(list, key) {
      var seen = {};
      var out = [];
      for (var n = 0; n < list.length; n++) {
        var val = list[n][key];
        if (val && !seen[val]) { seen[val] = 1; out.push(val); }
      }
      return out;
    }

    function filterBy(list, key, value) {
      if (!value) return list;
      var out = [];
      for (var n = 0; n < list.length; n++) {
        if (list[n][key] === value) { out.push(list[n]); }
      }
      return out;
    }

    function fill(select, values, _unused) {
      var placeholder = select.getAttribute('data-at-placeholder-option') ||
        (select.options.length ? select.options[0].textContent : '');
      if (!select.getAttribute('data-at-placeholder-option')) {
        select.setAttribute('data-at-placeholder-option', placeholder);
      }
      select.innerHTML = '';
      select.appendChild(new Option(placeholder, ''));
      for (var n = 0; n < values.length; n++) {
        var item = values[n];
        if (typeof item === 'string') {
          select.appendChild(new Option(item, item));
        } else {
          select.appendChild(new Option(item.label, item.value));
        }
      }
      select.disabled = values.length === 0;
    }
  }

  /* The panel CTA. Writes only a real garage value; otherwise it hands the
     customer to the app's own picker rather than guessing one. */
  function saveGarageSelection(button) {
    var value = valueOf('[data-at-garage-field="year"]');

    if (AtReskinGarage.write(value)) {
      var panel = one('[data-at-garage-panel]');
      if (panel) { setGarageOpen(panel, false); }

      /* A guest in private mode or over quota persisted NOTHING — the write is
         swallowed and no POST fires without a customerId — so a reload would
         come back to an empty garage and the pill would revert to the
         placeholder, the customer's click looking like it reset their choice.
         write() has already repainted the page in place, which at least
         reflects the ute they picked for this pageview. */
      if (!AtReskinGarage.persisted && !AtReskinGarage.appConfig().customerId) {
        window.console && console.warn('[at-reskin] vehicle could not be saved (storage unavailable)');
        return;
      }

      /* The garage drives collection filters and fitment badges rendered
         server-side, so the page has to come back to reflect the new vehicle.
         Wait (briefly) for the persistence POST rather than navigating out
         from under it. */
      var url = button.getAttribute('data-at-garage-url');
      var go = function () {
        if (url) { window.location.href = url; } else { window.location.reload(); }
      };
      var pending = AtReskinGarage.pending;
      if (pending && pending.then) {
        var done = false;
        var once = function () { if (!done) { done = true; go(); } };
        pending.then(once, once);
        window.setTimeout(once, 1500);
      } else {
        go();
      }
      return;
    }

    /* No configured selects / no authoritative pick: the CTA opens the SAME
       Find Your Fit selector as Shop by Ute — one door to choosing a vehicle. */
    var panelEl = one('[data-at-garage-panel]');
    if (panelEl) { setGarageOpen(panelEl, false); }
    var src = filterSrc();
    if (src || (window.Filter && window.Filter.open)) { openVehicleFilter(src, false); return; }
    gotoVehiclePicker(button);
  }

  /* Hand off to the garage app's own picker (window.Filter). When its controls
     are on this page AND reachable, scroll to them; otherwise go to the page
     that hosts it. See vehiclePickerElement() for what "reachable" means here —
     on this store the picker is modal-hosted. */
  function gotoVehiclePicker(button) {
    var target = vehiclePickerElement();
    if (target) {
      var panel = one('[data-at-garage-panel]');
      if (panel) { setGarageOpen(panel, false); }
      if (target.scrollIntoView) { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
      try { target.focus({ preventScroll: true }); } catch (e) {
        if (target.focus) { target.focus(); }
      }
      return;
    }
    var url = button.getAttribute('data-at-garage-picker-url') ||
              button.getAttribute('data-at-garage-url');
    if (url) { window.location.href = url; }
  }

  /* MODAL-HOSTED PICKER. layout/theme.liquid renders the app's picker through
     the modal-filter snippet on every template, so its nodes are in the DOM but
     inside a closed modal (a sibling of <dialog id="searchModal">). Returning
     one made gotoVehiclePicker() scrollIntoView() and focus() a node that is not
     rendered — two silent no-ops — and return before the picker-URL fallback
     was reached, so on a default install the CTA did nothing at all. So: open
     the modal when the node is inside one, skip a candidate that is present but
     not rendered, and return null when nothing usable is found so the caller
     falls through to data-at-garage-picker-url.

     VERIFY ON THE LIVE PAGE which node Filter.elms actually holds and whether
     its container is a <dialog> or a class-toggled div; if the latter, add its
     opener here. */
  function vehiclePickerElement() {
    var F = window.Filter;
    if (!F || !F.elms) return null;
    var candidates = [F.elms.fieldControls, F.elms.saveButton, F.elms.redirectButton];
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      if (!c) continue;
      var el = (c.nodeType === 1) ? c : (c.length ? c[0] : null);
      if (!el || el.nodeType !== 1) continue;

      var dlg = el.closest ? el.closest('dialog') : null;
      if (dlg && !dlg.open) {
        try {
          if (dlg.showModal) { dlg.showModal(); }
          else if (dlg.show) { dlg.show(); }
          else { dlg.setAttribute('open', ''); }
        } catch (e) { dlg.setAttribute('open', ''); }
        return el;
      }

      /* offsetParent is null for a display:none node AND for a position:fixed
         one, so the rect count decides the second case. */
      if (!el.offsetParent && (!el.getClientRects || el.getClientRects().length === 0)) {
        continue;
      }
      return el;
    }
    return null;
  }

  function valueOf(selector) {
    var el = one(selector);
    return el ? el.value : '';
  }

  /* =======================================================================
     7. PROMO BAR
     ======================================================================= */

  function initPromo() {
    var promo = one('[data-at-promo]');
    if (!promo) return;
    var key = 'at_promo_' + (promo.getAttribute('data-at-promo-key') || 'default');

    /* The bar renders VISIBLE from Liquid and this only ever hides it. It used
       to render with a hard `hidden` attribute and rely on this line to reveal
       it, which meant a blocked, failed or late script left the promo bar off
       the page entirely — the one piece of the design that depended on JS to
       exist at all. */
    var dismissed = false;
    try { dismissed = window.localStorage.getItem(key) === '1'; } catch (e) {}
    if (dismissed) { promo.hidden = true; }

    promo.addEventListener('click', function (e) {
      if (!e.target.closest || !e.target.closest('[data-at-promo-dismiss]')) return;
      promo.hidden = true;
      try { window.localStorage.setItem(key, '1'); } catch (err) {}
    });
  }

  /* =======================================================================
     8. JUDGE.ME GLUE
     ======================================================================= */

  /* Shared vehicle-selector opener: loads the theme's filter.js once and
     calls Filter.open(). Used by the Shop by Ute nav trigger AND the garage
     panel's Choose-your-ute CTA. A nav open (navOpen=true) picks its landing
     screen first — see prepareNavOpen(); the garage CTA opens the dialog
     exactly where it was left. */
  var filterLoading = false;
  function openVehicleFilter(src, navOpen) {
      if (window.Filter && window.Filter.open) { if (navOpen) { prepareNavOpen(); } window.Filter.open(); return; }
      if (!document.getElementById('modal-filter-template') && !document.querySelector('.modal-ui')) {
        if (window.AtReskin && AtReskin.garage && AtReskin.garage.gotoVehiclePicker) AtReskin.garage.gotoVehiclePicker();
        return;
      }
      if (filterLoading || !src) return;
      filterLoading = true;
      var sc = document.createElement('script');
      sc.src = src;
      sc.onload = function () {
        var tries = 0;
        (function poll() {
          if (window.Filter && window.Filter.open) { if (navOpen) { prepareNavOpen(); } window.Filter.open(); return; }
          if (++tries < 40) window.setTimeout(poll, 100);
        })();
      };
      document.head.appendChild(sc);
  }

  /* Shop by Ute's landing screen. A saved ute means the press lands on the
     final confirmation screen (the has-results view — the one with the Clear
     button); with nothing saved it lands on "What's the make?". Never the
     half-way tab the visitor happened to close the dialog on. */
  function prepareNavOpen() {
    var F = window.Filter;
    if (!F || !F.elms) return;
    var container = F.elms.filterContainer;
    /* Already showing the confirmation — that IS the target screen. */
    if (container && container.classList.contains('has-results')) return;
    var entry = '';
    try { entry = AtReskinGarage.valueOfEntry(AtReskinGarage.savedEntry()) || ''; } catch (e) {}
    if (!entry) { resetFilterForm(); return; }
    if (F.data && F.data.length) {
      if (!driveFilterToEntry(entry)) { resetFilterForm(); }
      return;
    }
    /* First open on this page: the vehicle JSON is still downloading. Show
       the start screen now and switch to the confirmation when it lands —
       the options are empty until then, so nothing can be half-picked in
       the meantime. */
    resetFilterForm();
    var tries = 0;
    (function waitData() {
      if (F.data && F.data.length) { driveFilterToEntry(entry); return; }
      if (++tries < 50) { window.setTimeout(waitData, 100); }
    })();
  }

  /* Re-select a saved "handle|cab" garage entry through the filter's own
     fields and land on the confirmation screen. Mirrors Filter.setValue()
     but joins field_4 years as the plain strings they are — setValue() reads
     a .name those strings don't have, so it cannot restore year-level
     handles. Returns false when the entry no longer resolves against the
     vehicle data, so the caller can fall back to the start screen. */
  function driveFilterToEntry(value) {
    try {
      var F = window.Filter;
      if (!F || !F.elms || !F.data || !F.data.length) return false;
      var parts = String(value).split('|');
      var savedHandle = (parts[0] || '').trim();
      var cab = (parts[1] || '').trim();
      if (!savedHandle) return false;
      var found = null;
      F.data.some(function (mk) {
        return (mk.field_2 || []).some(function (md) {
          return (md.field_3 || []).some(function (bd) {
            if (F.handle([mk.name, md.name, bd.name].join(' ')) === savedHandle) {
              found = { make: mk, model: md, body: bd, year: '' };
              return true;
            }
            return (bd.field_4 || []).some(function (yr) {
              if (F.handle([mk.name, md.name, bd.name, yr].join(' ')) === savedHandle) {
                found = { make: mk, model: md, body: bd, year: yr };
                return true;
              }
              return false;
            });
          });
        });
      });
      if (!found) return false;
      var fc = F.elms.fieldControls;
      fc[0].innerHTML = found.make.name;
      fc[0].setAttribute('data-value', found.make.name);
      F.initField(1, found.make.field_2);
      fc[1].innerHTML = found.model.name;
      fc[1].setAttribute('data-value', found.model.name);
      F.initField(2, found.model.field_3);
      fc[2].innerHTML = found.body.name;
      fc[2].setAttribute('data-value', found.body.name);
      if (found.year) {
        F.initField(3, found.body.field_4, true);
        fc[3].innerHTML = found.year;
        fc[3].setAttribute('data-value', found.year);
      } else {
        F.hiddenField(3);
        fc[3].innerHTML = fc[3].getAttribute('data-placeholder') || '';
        fc[3].setAttribute('data-value', '');
      }
      fc[4].classList.remove('d-none');
      var cabLi = null;
      var lis = F.elms.fieldContents[4].querySelectorAll('.js-filter-options > li');
      for (var i = 0; i < lis.length; i++) {
        if ((lis[i].getAttribute('data-value') || '') === cab) { cabLi = lis[i]; break; }
      }
      if (cabLi) {
        /* The li's own handler labels field 5 and runs getResults(), which
           ends in setStatus("has-results") — the confirmation screen. */
        cabLi.click();
      } else {
        fc[4].setAttribute('data-value', cab);
        if (cab) { fc[4].innerHTML = cab; }
        F.getResults();
      }
      return true;
    } catch (e) { return false; }
  }

  /* Reset the Find Your Fit FORM only (never the saved garage) — mirrors the
     theme's own Clear handler, so the dialog starts back at "What's the
     make?". */
  function resetFilterForm() {
    try {
      var F = window.Filter;
      if (!F || !F.elms) { return; }
      [4, 3, 2, 1, 0].forEach(function (n) { try { F.resetField(n); } catch (e) {} });
      var fcs = F.elms.fieldControls, fcts = F.elms.fieldContents;
      [].forEach.call(fcs, function (c, i) {
        c.classList.remove('d-none');
        c.classList.toggle('active', i === 0);
        if (fcts && fcts[i]) { fcts[i].classList.toggle('active', i === 0); }
      });
      var sc = F.elms.filterContainer;
      if (sc) { sc.classList.remove('has-results', 'no-results', 'pending'); }
      try { F.message(); } catch (e) {}
    } catch (e) {}
  }

  function filterSrc() {
    var el = one('[data-at-filter-src]');
    return el ? el.getAttribute('data-at-filter-src') : null;
  }

  /* The saved ute's products page — the same URL shape the filter's own View
     Products button builds: /collections/<handle>/<cab-tag>. */
  function garageCollectionUrl(entry) {
    var parts = String(entry).split('|');
    var handle = (parts[0] || '').trim();
    var cab = (parts[1] || '').trim().toLowerCase().replace(/[^\w]+/g, '-').replace(/^-+|-+$/g, '');
    if (!handle) return '';
    var base = (window.theme && theme.routes && theme.routes.collections_url) || '/collections';
    return base + '/' + handle + (cab ? '/' + cab : '');
  }

  /* ============================= MEGA NAV ============================= */
  function initMegaNav() {
    function closeAll(except) {
      each('[data-at-mega-panel]', function (p) {
        if (p.getAttribute('data-at-mega-panel') === except) return;
        p.hidden = true;
      });
      each('[data-at-mega-toggle]', function (t) {
        if (t.getAttribute('data-at-mega-toggle') === except) return;
        t.setAttribute('aria-expanded', 'false');
      });
    }
    document.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target.closest('[data-at-mega-toggle]') : null;
      if (t) {
        var key = t.getAttribute('data-at-mega-toggle');
        var panel = one('[data-at-mega-panel="' + key + '"]');
        if (!panel) return;
        var opening = panel.hidden;
        closeAll(null);
        panel.hidden = !opening;
        t.setAttribute('aria-expanded', opening ? 'true' : 'false');
        return;
      }
      /* Click outside any open panel closes it. */
      if (!e.target.closest || !e.target.closest('[data-at-mega-panel]')) closeAll(null);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAll(null);
    });
    /* Shop by Ute opens the theme's vehicle-filter dialog DIRECTLY.
       filter.js self-instantiates window.Filter from #modal-filter-template
       (the layout renders that template on every page) and exposes
       Filter.open(), so the trigger loads the script on first click and calls
       open() — no dependence on the old header's hidden lazy-load wiring. The
       js-filter-button class is kept so the theme's results preload still
       warms up. navOpen=true: a saved ute lands the press on the confirmation
       screen, nothing saved lands it on the start screen. */
    document.addEventListener('click', function (e) {
      /* a.js-filter-button is the theme editor's "open the vehicle filter"
         button class (e.g. the home page SHOP ALL PRODUCTS block). Those
         buttons carry no data attributes, so borrow filter.js's URL from
         any header element that declares it. */
      var b = e.target.closest ? e.target.closest('[data-at-shopbyute], a.js-filter-button') : null;
      if (!b) return;
      e.preventDefault();
      var src = b.getAttribute('data-at-filter-src');
      if (!src) {
        var carrier = document.querySelector('[data-at-filter-src]');
        if (carrier) src = carrier.getAttribute('data-at-filter-src');
      }
      openVehicleFilter(src, true);
    });
  }
  initMegaNav();

  /* Final band CTA. "Get Yours" IS an add to cart: it clicks the page's own
     Add to Cart button, so the selected variant, the drawer, the loading
     state and the disabled/unavailable state all behave exactly like the
     real button — no second add-to-cart code path. The section renders
     [data-at-get-yours] only when its button URL setting is blank, so a
     configured URL bypasses this entirely. Off a product page (the section
     dropped on another template) there is no ATC; fall back to scrolling to
     the buy area if one exists, else leave the '#' href alone. */
  document.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest('[data-at-get-yours]') : null;
    if (!b) return;
    var atc = one('.at-reskin .at-atc');
    if (atc) {
      e.preventDefault();
      atc.click();
      return;
    }
    var target = one('[data-section-type="at-reskin-pdp"]');
    if (!target) return;
    e.preventDefault();
    if (target.scrollIntoView) { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
  });

  /* ===================== RESULTS PAGE (at-srp) ===================== */
  /* Quick-add: adds the card's first available variant and stays on the
     page — the results page is a comparison, so no drawer and no
     navigation (RESULTS-PAGE.md). The nav cart count re-reads /cart.js so
     it can never drift from the real cart. */
  document.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest('[data-at-quickadd]') : null;
    if (!b || b.disabled) return;
    e.preventDefault();
    b.disabled = true;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(b.getAttribute('data-at-quickadd'), 10), quantity: 1 }] })
    }).then(function (r) {
      if (!r.ok) { throw new Error('add failed'); }
      return fetch('/cart.js').then(function (cr) { return cr.json(); });
    }).then(function (cart) {
      each('[data-at-cart-count]', function (el) { el.textContent = cart.item_count; });
      /* Success feedback ON the button: + trolley becomes a tick and the
         button goes green, so the response is where the click happened. */
      b.classList.add('at-srp__quickadd--done');
      var tr = b.querySelector('[data-at-qatrolley]');
      if (tr) { tr.hidden = true; }
      var tk = b.querySelector('[data-at-qatick]');
      if (tk) { tk.hidden = false; }
      b.setAttribute('aria-label', 'Added to cart');
      var added = b.parentNode.querySelector('[data-at-added]');
      if (added) { added.hidden = false; }
      b.disabled = false;
    }).catch(function () { b.disabled = false; });
  });

  /* Liner toggle (mats band): "What's in your tub?" chips filter the mat
     cards client-side — each card wrapper carries data-at-liner and only
     the picked bucket stays visible. Buckets that don't exist for the
     vehicle never render a chip, so every pick shows at least one mat. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-at-liner-pick]') : null;
    if (!btn) return;
    var tg = btn.closest('[data-at-linertoggle]');
    var scope = btn.closest('section') || document;
    var key = btn.getAttribute('data-at-liner-pick');
    Array.prototype.forEach.call(tg.querySelectorAll('[data-at-liner-pick]'), function (b) {
      b.classList.toggle('at-srp__linerbtn--on', b === btn);
      b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
    });
    Array.prototype.forEach.call(scope.querySelectorAll('[data-at-liner]'), function (w) {
      w.hidden = w.getAttribute('data-at-liner') !== key;
    });
  });

  /* Tub-setup switcher: the Change button opens the small panel of setups
     that actually exist on this vehicle; each option is a plain link to the
     tag-filtered collection URL, so the whole page recomputes server-side. */
  document.addEventListener('click', function (e) {
    var t = e.target.closest ? e.target.closest('[data-at-setup-toggle]') : null;
    var panel = one('[data-at-setup-panel]');
    if (!panel) return;
    if (t) {
      e.preventDefault();
      panel.hidden = !panel.hidden;
      t.setAttribute('aria-expanded', panel.hidden ? 'false' : 'true');
      return;
    }
    if (!panel.hidden && !e.target.closest('[data-at-setup-panel]')) { panel.hidden = true; }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var panel = one('[data-at-setup-panel]');
    if (panel && !panel.hidden) { panel.hidden = true; }
  });

  /* ====================== CART PAGE (at-cart) ====================== */
  /* Steppers, removes and the rule-driven Add-On Offer block (CARTPAGE.md).
     Every mutation goes through the Cart AJAX API and then reloads the page:
     the offer discounts are native automatic discounts that Shopify applies
     server-side, so a fresh Liquid render is the one source of truth for
     prices, allocations and the ADD-ON OFFER APPLIED labels. */
  function initCartPage() {
    var page = one('[data-at-cart-page]');
    if (!page) return;

    function changeLine(key, qty) {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      }).then(function (r) { if (r.ok) { window.location.reload(); } });
    }
    page.addEventListener('click', function (e) {
      var qb = e.target.closest('[data-at-qty-btn]');
      if (qb) {
        var next = parseInt(qb.getAttribute('data-qty'), 10) + parseInt(qb.getAttribute('data-dir'), 10);
        changeLine(qb.getAttribute('data-key'), Math.max(0, next));
        return;
      }
      var rm = e.target.closest('[data-at-line-remove]');
      if (rm) { changeLine(rm.getAttribute('data-key'), 0); }
    });

    /* Vehicle name in the meta line — from the garage, like every page. */
    try {
      var v = AtReskinGarage.read();
      var slot = one('[data-at-cart-vehicle]');
      if (v && v.label && slot) { slot.textContent = ' for your ' + v.label; }
    } catch (err) {}

    initCartOffers(page);
  }

  /* The offer rules, evaluated against the live cart:
       1. cover or canopy in cart, no mat  → the mat cut for the garage ute
       2. mat in cart, no cover, no canopy → the cover AND canopy that fit
       3. cover or canopy in cart, no 303  → the 303 protectant
     Never a product already in the cart; never one that does not fit the
     selected ute; at most three cards; nothing renders when no rule fires. */
  var CART_COVER_RE = /tonneau|bunji|rope|cover/;
  var CART_CANOPY_RE = /canopy/;
  function initCartOffers(page) {
    var block = one('[data-at-offers]');
    var grid = one('[data-at-offergrid]');
    if (!block || !grid) return;
    var pct = parseInt(block.getAttribute('data-offer-pct'), 10) || 10;
    var p303handle = block.getAttribute('data-p303-handle') || '';
    /* Discount claims render only when the automatic discounts exist (the
       section setting says so) — otherwise the cards upsell honestly at the
       normal price and the header drops the savings promise. */
    var discOn = block.getAttribute('data-offer-discount') === '1';
    if (!discOn) {
      var eb = block.querySelector('.at-cart__offerseyebrow');
      var tt = block.querySelector('.at-cart__offerstitle');
      if (eb) { eb.textContent = 'Add-Ons'; }
      if (tt) { tt.textContent = 'Goes with what\u2019s in your cart'; }
    }

    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      if (!cart.items.length) return;
      var hasCover = false, hasCanopy = false, hasMat = false, has303 = false;
      var inCart = {};
      cart.items.forEach(function (it) {
        var t = (it.product_type || '').toLowerCase();
        inCart[it.handle] = true;
        if (CART_CANOPY_RE.test(t)) { hasCanopy = true; }
        else if (t.indexOf('mat') > -1) { hasMat = true; }
        else if (CART_COVER_RE.test(t)) { hasCover = true; }
        if (it.handle === p303handle) { has303 = true; }
      });
      var qualWord = hasCanopy && !hasCover ? 'a canopy' : 'a tonneau cover';

      var vehicle = null;
      try { vehicle = AtReskinGarage.read(); } catch (err) {}

      var jobs = [];
      /* vehicle products, only when a rule will need them */
      var needVehicle = vehicle && vehicle.handle &&
        (((hasCover || hasCanopy) && !hasMat) || (hasMat && !hasCover && !hasCanopy));
      if (needVehicle) {
        jobs.push(fetch('/collections/' + vehicle.handle + '/products.json?limit=250')
          .then(function (r) { return r.ok ? r.json() : { products: [] }; })
          .catch(function () { return { products: [] }; }));
      } else { jobs.push(Promise.resolve({ products: [] })); }
      if ((hasCover || hasCanopy) && !has303 && p303handle) {
        jobs.push(fetch('/products/' + p303handle + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; }));
      } else { jobs.push(Promise.resolve(null)); }

      Promise.all(jobs).then(function (res) {
        var prods = res[0].products || [];
        var p303 = res[1];
        var cab = (vehicle && vehicle.cab || '').toUpperCase();
        function firstOf(test) {
          for (var i = 0; i < prods.length; i++) {
            var p = prods[i];
            if (inCart[p.handle]) continue;
            var t = (p.product_type || '').toLowerCase();
            var va = null;
            for (var j = 0; j < p.variants.length; j++) { if (p.variants[j].available) { va = p.variants[j]; break; } }
            if (!va) continue;
            if (test(t, p.tags || [])) { return { p: p, v: va }; }
          }
          return null;
        }
        var fitsCab = function (tags) { return !cab || tags.indexOf(cab) > -1; };
        var offers = [];
        if ((hasCover || hasCanopy) && !hasMat && prods.length) {
          var mat = firstOf(function (t, tags) { return t.indexOf('mat') > -1 && fitsCab(tags); });
          if (mat) { offers.push(card(mat, 'Because your cart has ' + qualWord, 'Cut for your tub, it stops load slide and protects the paint under the cover.')); }
        }
        if ((hasCover || hasCanopy) && !has303 && p303) {
          offers.push(card({ p: p303, v: firstAvail(p303) }, 'Because your cart has ' + qualWord, 'A few sprays a season keeps the fabric UV-safe and looking new.'));
        }
        if (hasMat && !hasCover && !hasCanopy && prods.length) {
          var cov = firstOf(function (t, tags) { return CART_COVER_RE.test(t) && !CART_CANOPY_RE.test(t) && fitsCab(tags); });
          if (cov) { offers.push(card(cov, 'Because your cart has a ute mat', 'Seals the tub the mat lives in — cut for the same ute.')); }
          var can = firstOf(function (t) { return CART_CANOPY_RE.test(t); });
          if (can) { offers.push(card(can, 'Because your cart has a ute mat', 'Full-height weather cover for the same tub, rolls up on three sides.')); }
        }
        offers = offers.filter(Boolean).slice(0, 3);
        if (!offers.length) return;
        grid.innerHTML = offers.join('');
        block.hidden = false;
      });

      function firstAvail(pjs) {
        /* /products/x.js variant objects: price in CENTS */
        for (var i = 0; i < pjs.variants.length; i++) { if (pjs.variants[i].available) return pjs.variants[i]; }
        return pjs.variants[0];
      }
      function money(cents) { return '$' + (cents / 100).toFixed(2); }
      function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;'); }
      function card(hit, reason, note) {
        if (!hit || !hit.v) return '';
        var p = hit.p, v = hit.v;
        /* products.json prices are dollar strings; product .js prices are
           cents — normalise to cents. */
        var priceC = typeof v.price === 'string' ? Math.round(parseFloat(v.price) * 100) : v.price;
        var offerC = Math.round(priceC * (100 - pct) / 100);
        if (!discOn) { offerC = priceC; }
        var img = (p.images && p.images[0] && (p.images[0].src || p.images[0])) || (p.featured_image || '');
        var name = String(p.title).split(/ to fit /i)[0];
        return '<article class="at-cart__offer">' +
          '<a class="at-cart__offerimg" href="/products/' + esc(p.handle) + '">' + (img ? '<img src="' + esc(img) + '" alt="" loading="lazy">' : '') + '</a>' +
          '<div class="at-cart__offerbody">' +
            (discOn ? '<span class="at-cart__offerchip">' + pct + '% off in this order</span>' : '') +
            '<div class="at-cart__offername">' + esc(name) + '</div>' +
            '<div class="at-cart__offerreason">' + esc(reason) + '</div>' +
            '<div class="at-cart__offernote">' + esc(note) + '</div>' +
            '<div class="at-cart__offerfoot">' +
              '<div class="at-cart__offerpricing">' +
                '<span class="at-cart__offerprice">' + money(offerC) + '</span>' +
                (discOn ? '<span class="at-cart__offerstruck">' + money(priceC) + '</span>' +
                          '<span class="at-cart__offersave">Save ' + money(priceC - offerC) + '</span>' : '') +
              '</div>' +
              '<button type="button" class="at-cart__offeradd" data-at-offer-add="' + v.id + '">Add to cart</button>' +
            '</div>' +
          '</div>' +
        '</article>';
      }
    });

    page.addEventListener('click', function (e) {
      var b = e.target.closest('[data-at-offer-add]');
      if (!b) return;
      b.disabled = true;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(b.getAttribute('data-at-offer-add'), 10), quantity: 1 }] })
      }).then(function (r) {
        if (r.ok) { window.location.reload(); } else { b.disabled = false; }
      }).catch(function () { b.disabled = false; });
    });
  }
  initCartPage();

  function initJudgeme() {
    var rating = one('[data-at-rating]');

    /* Swap the plain star fallback out once Judge.me has painted its badge. */
    if (rating) {
      var started = Date.now();
      var poll = window.setInterval(function () {
        if (rating.querySelector('.jdgm-prev-badge')) {
          /* The badge is display:none by design now (our ink-star row is the
             rating everywhere) — the poll only confirms it exists for the
             deep-link click handler; jdgm-ready is no longer added. */
          window.clearInterval(poll);
        } else if (Date.now() - started > AT_RESKIN_CONFIG.judgemeTimeout) {
          window.clearInterval(poll);
        }
      }, 300);
    }

    /* "N reviews" and "Read all N reviews" scroll to the widget rather than
       leaving the page. */
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var trigger = e.target.closest('[data-at-scroll-reviews], [data-at-show-reviews]');
      if (!trigger) return;
      var target = one('[data-at-reviews-widget]') || one('#at-reviews');
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /* =======================================================================
     BOOT
     ======================================================================= */

  function boot() {
    initVariants();
    initGallery();
    initCart();
    initStickyBar();
    initGarage();
    initPromo();
    initJudgeme();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* THEME CUSTOMIZER. Shopify re-renders a section's DOM when the merchant
     edits it and does NOT re-execute scripts re-inserted that way, so the
     replacement markup arrives unpainted: the pill back to its placeholder,
     the cart badge back to the count Liquid rendered. Every listener above is
     delegated off document and every node is resolved at use time, so the only
     thing needed here is a repaint. */
  document.addEventListener('shopify:section:load', function (e) {
    var t = e.target;
    if (!t || !t.querySelector) return;
    if (!t.querySelector('[data-at-header], [data-at-pdp], [data-at-promo]')) return;
    AtReskinGarage.paint(AtReskinGarage.read());
    refreshCart();
  });

  /* Two headers on one page means two garage panels, two cart badges and two
     copies of every hook — one() binds the first, so the in-page pill silently
     drives the other panel. It happens when the layout/theme.liquid swap and
     the template's own promo/header sections are both enabled; see
     UPLOAD-MANIFEST.md §5. */
  if (document.querySelectorAll('[data-at-header]').length > 1) {
    window.console && console.warn(
      '[at-reskin] more than one reskin header on the page — enable EITHER the ' +
      'layout/theme.liquid swap OR the template sections, not both (see §5).');
  }

  /* Exposed so the garage app and any future integration can reach the same
     seams without editing this file. Populated, not replaced — the guard flag
     at the top of the file is this same object and it is already armed. */
  window.AtReskin.config = AT_RESKIN_CONFIG;
  window.AtReskin.garage = AtReskinGarage;
  window.AtReskin.cart = AtReskinCart;
  window.AtReskin.refreshCart = refreshCart;
  window.AtReskin.openDrawer = openDrawer;
  window.AtReskin.closeDrawer = closeDrawer;
  window.AtReskin.selectVariant = selectVariant;
  window.AtReskin.formatMoney = formatMoney;
})();
